import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Form, Spin, Modal } from '@whalecloud/fdx';
import { useSelector } from 'react-redux';
import { i18n } from 'fishx';
import {
  renderFormItems,
  addressInfoForSubsFormItems,
  billAccountInfoForSubsFormItems,
  serviceBasicInfoForSubsFormItems,
  planInfoForSubsFormItems,
  getBusinessI18n,
  getPlanTimeUnitList,
  doCurrencyPrecision,
  fixZeroPrecision,
} from '@/utils';
import { StepFooter } from '@/components';
import styles from './index.module.less';

function SubscriberInfo(props) {
  const {
    onNext,
    onPrevious,
    form,
    dispatch,
    modelKey,
    selectSubsInfo = {},
    acctDetail = {},
    subsDetail = {},
    planDetail = {},
  } = props;
  const { DRM_DEF_CURRENCY_PRECISION, DRM_CURRENCY_SHOW_PRECISION } = useSelector(state => state.business);
  const [loading, setLoading] = useState(false);

  const { getFieldDecorator, getFieldsValue } = form;

  const useMemoBillAccountInfoForSubsFormItems = useMemo(
    () =>
      billAccountInfoForSubsFormItems({
        disabled: true,
        defaultValueMap: acctDetail,
      }),
    [acctDetail]
  );

  const useMemoServiceBasicInfoForSubsFormItems = useMemo(
    () =>
      serviceBasicInfoForSubsFormItems({
        disabled: true,
        defaultValueMap: { ...subsDetail, ...selectSubsInfo },
      }),
    [subsDetail]
  );

  const useMemoAddressInfoForSubsFormItems = useMemo(
    () =>
      addressInfoForSubsFormItems({
        disabled: true,
        defaultValueMap: get(subsDetail, 'installationAddressDto') || {},
      }),
    [subsDetail]
  );

  const useMemoPlanInfoForSubsFormItems = useMemo(
    () =>
      planInfoForSubsFormItems({
        disabled: true,
        defaultValueMap: get(planDetail, 'subsPlanDto') || {},
        offerName: get(subsDetail, 'offerName'),
        planTimeUnitList: getPlanTimeUnitList(i18n),
      }),
    [planDetail, subsDetail]
  );

  const handleGetSubsInfo = () => {
    const {
      EXP_ACCOUNT_TYPE,
      EXP_ACCT_SUB_TYPE,
      acctNbr,
      billingFreq,
      billingMedia,
      mobileNumber,
      emailId,
      serviceType,
      subServiceType,
      accNbr,
      status,
      houseNo,
      building,
      postCode,
      state,
      district,
      mainLocality,
      subLocality,
      landmark,
      latitude,
      longitude,
      planName,
      price,
      cycleQuantity,
    } = getFieldsValue();
    const subsReviewDetail = {
      billingAccountInfo: {
        accType: EXP_ACCOUNT_TYPE,
        acctSubsType: EXP_ACCT_SUB_TYPE,
        accountNo: acctNbr,
        frequecy: billingFreq,
        media: billingMedia,
        mobileNumber,
        eMail: emailId,
      },
      serviceInfo: {
        serviceType,
        subsServiceType: subServiceType,
        serviceNumber: accNbr,
        status,
      },
      addressInfo: {
        faltHoustNo: houseNo,
        apartmentBuilding: building,
        pinCode: postCode,
        state,
        cityDistrict: district,
        mainLocality,
        subLocality,
        landmark,
        latitude,
        longitude,
      },
      planInfo: {
        planName,
        price,
        cycleQuantity,
      },
    };
    return subsReviewDetail;
  };

  useEffect(() => {
    if (isEmpty(subsDetail)) {
      setLoading(true);
    }
    // 查询订户详情
    dispatch({
      type: 'business/qrySubsDetail',
      payload: {
        subsId: selectSubsInfo.subsId,
        addressFlag: 'Y',
        subsPlanFlag: 'Y',
      },
      modelName: modelKey,
    }).then(async subs => {
      if (subs.accNbr) {
        // 优化，有号码才继续查询
        // 查询可选账单介质
        dispatch({ type: 'business/qryBilldeliveryMethod', modelName: modelKey });
        // 查询账户详情
        await dispatch({
          type: 'business/qryAcctDetail',
          payload: {
            acctNbr: subs.acctNbr,
          },
          modelName: modelKey,
        });
        // 查询套餐详情
        await dispatch({
          type: 'business/qrySubsPlanDetail',
          payload: {
            subsPlanId: subs.offerId,
            addressId: get(subs, 'installationAddressDto.custAddressId'),
          },
          modelName: modelKey,
        });
      } else {
        // 报错回退页面
        onPrevious();
      }
      setLoading(false);
    });
  }, []);

  const handleContinue = async () => {
    if (modelKey === 'disconnection') {
      // 判断协议是否结束
      const terminationCheckRes = await dispatch({
        type: 'business/terminationCheckBsnl',
        payload: {
          subsId: subsDetail.subsId,
          subsEventId: '46',
        },
      });
      if (terminationCheckRes.data && terminationCheckRes.data.agreementFlag === 'Y') {
        Modal.warning({
          content: terminationCheckRes?.data?.descMsg || 'Subscriber agreement period verification',
        });
        // 需求需要  提示,但是可以继续拆机
        // return;
      }

      // 如果是拆机需要查看欠费情况
      const rtn = await dispatch({
        type: 'business/queryAccountBsnl',
        payload: {
          accountCode: subsDetail.acctNbr,
        },
      });
      if (+rtn.totalDue > 0) {
        Modal.info({
          title: getBusinessI18n('OUTSTANDING_DETAILS'),
          content: (
            <div className={styles.detailWarp}>
              <div className={styles.detailTop}>
                <div className={styles.detailTopRow}>
                  <span>{getBusinessI18n('SERVICE_NUMBER')}</span>
                  <span>{subsDetail.accNbr}</span>
                </div>
                <div className={styles.detailTopRow}>
                  <span>{getBusinessI18n('ACCOUNT_NUMBER')}</span>
                  <span>{subsDetail.acctNbr}</span>
                </div>
              </div>
              <div className={styles.detailBottom}>
                <div className={styles.detailBottomRow}>
                  <span>{getBusinessI18n('TOTAL_OUTSTANDING')}</span>
                  <span>
                    {fixZeroPrecision(
                      doCurrencyPrecision(+rtn.totalDue, DRM_DEF_CURRENCY_PRECISION),
                      DRM_CURRENCY_SHOW_PRECISION,
                      true
                    )}
                  </span>
                </div>
              </div>
            </div>
          ),
          className: 'alartWarp hasTitleAlartWarp',
          okText: i18n('COMMON_CONFIRM'),
          width: 566,
        });
      }
    }
    const qrySubsInfo = handleGetSubsInfo();
    await dispatch({
      // 存到modelKey中，主要Review使用
      type: `${modelKey}/save`,
      payload: {
        subsReviewDetail: qrySubsInfo,
      },
    });
    onNext();
  };

  const handlePrevious = () => {
    // 清空订户详情，以便下次展示加载
    dispatch({
      type: `${modelKey}/save`,
      payload: {
        subsDetail: {},
      },
    });
    onPrevious();
  };

  return (
    <Spin spinning={loading}>
      <div className="threeColumnWrap">
        <Form layout="inline">
          <div className="blockWrap">
            <h3 className="blockTitle">{getBusinessI18n('BILLING_ACCOUNT_INFO')}</h3>
            <div className="formItemBox">{renderFormItems(useMemoBillAccountInfoForSubsFormItems, getFieldDecorator)}</div>
          </div>
          <div className="blockWrap">
            <h3 className="blockTitle">{getBusinessI18n('SERVICE_INFO')}</h3>
            <h4 className="blockSubTitle">{getBusinessI18n('BASIC_INFO')}</h4>
            <div className="formItemBox">{renderFormItems(useMemoServiceBasicInfoForSubsFormItems, getFieldDecorator)}</div>
            {!isEmpty(get(subsDetail, 'installationAddressDto')) ? (
              <>
                <h4 className="blockSubTitle">
                  {['shift', 'transfer', 'reconnection'].includes(modelKey)
                    ? getBusinessI18n('OLD_INSTALLATION_INFO')
                    : getBusinessI18n('INSTALLATION_INFO')}
                </h4>
                <div className="formItemBox">{renderFormItems(useMemoAddressInfoForSubsFormItems, getFieldDecorator)}</div>
              </>
            ) : null}
          </div>
          <div className="blockWrap">
            <h3 className="blockTitle">{getBusinessI18n('PLAN_INFO')}</h3>
            <div className="formItemBox">{renderFormItems(useMemoPlanInfoForSubsFormItems, getFieldDecorator)}</div>
          </div>
          <StepFooter onContinue={handleContinue} onPrevious={handlePrevious} />
        </Form>
      </div>
    </Spin>
  );
}
export default Form.create({ name: 'subscriberInfo' })(SubscriberInfo);
